import { renderRcdIcons } from "../core/icons";

const numberValue = (element) => {
    const value = Number.parseFloat(element?.value ?? "0");
    return Number.isFinite(value) ? value : 0;
};

const money = (value) => value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const setupQuoteWorkspace = (root = document) => {
    const workspace = root.querySelector?.("[data-quote-workspace]") ?? document.querySelector("[data-quote-workspace]");
    if (!workspace || workspace.dataset.quoteReady === "true") return;
    workspace.dataset.quoteReady = "true";

    const form = workspace.querySelector("[data-quote-form]");
    const lines = workspace.querySelector("[data-quote-lines]");
    const template = workspace.querySelector("[data-quote-line-template]");
    const taxMode = workspace.querySelector("[data-quote-tax-mode]");
    const defaultTaxRate = numberValue(taxMode?.dataset.defaultTaxRate);

    const reindex = () => {
        lines?.querySelectorAll("[data-quote-line]").forEach((row, index) => {
            row.querySelectorAll("[name]").forEach((field) => {
                field.name = field.name.replace(/Form\.Lines\[(?:\d+|__index__)\]/, `Form.Lines[${index}]`);
            });
        });
    };

    const calculate = () => {
        let totalCost = 0, grossProfit = 0, discountTotal = 0, subtotal = 0, taxTotal = 0, total = 0;
        const isTaxInclusive = taxMode?.value === "Inclusive";
        lines?.querySelectorAll("[data-quote-line]").forEach((row) => {
            const quantity = numberValue(row.querySelector("[data-line-quantity]"));
            const cost = numberValue(row.querySelector("[data-line-cost]"));
            const price = numberValue(row.querySelector("[data-line-price]"));
            const discountPercent = numberValue(row.querySelector("[data-line-discount]"));
            const taxRate = numberValue(row.querySelector("[data-line-tax]"));
            const gross = quantity * price;
            const discount = gross * discountPercent / 100;
            const net = gross - discount;
            const tax = isTaxInclusive ? net - (net / (1 + taxRate / 100)) : net * taxRate / 100;
            const lineTotal = isTaxInclusive ? net : net + tax;
            const netOfTax = lineTotal - tax;
            const lineCost = quantity * cost;
            const profit = netOfTax - lineCost;
            totalCost += lineCost; grossProfit += profit; discountTotal += discount; subtotal += netOfTax; taxTotal += tax; total += lineTotal;
            const profitCell = row.querySelector("[data-line-profit]");
            const amountCell = row.querySelector("[data-line-amount]");
            if (profitCell) profitCell.textContent = money(profit);
            if (amountCell) amountCell.textContent = money(lineTotal);
        });
        const values = {
            "[data-quote-total-cost]": totalCost, "[data-quote-total-profit]": grossProfit,
            "[data-quote-total-discount]": discountTotal, "[data-quote-subtotal]": subtotal,
            "[data-quote-tax]": taxTotal, "[data-quote-total]": total
        };
        Object.entries(values).forEach(([selector, value]) => { const target = workspace.querySelector(selector); if (target) target.textContent = money(value); });
    };

    const addLine = (data = {}) => {
        if (!lines || !template) return null;
        const index = lines.querySelectorAll("[data-quote-line]").length;
        const holder = document.createElement("tbody");
        holder.innerHTML = template.innerHTML.replaceAll("__index__", String(index)).trim();
        const row = holder.firstElementChild;
        if (!row) return null;
        const set = (selector, value) => { const field = row.querySelector(selector); if (field) field.value = value ?? ""; };
        set("[data-line-code]", data.code); set("[data-line-source]", data.source || "Miscellaneous");
        set("[data-line-source-id]", data.id); set("[data-line-description]", data.description);
        set("[data-line-unit]", data.unit || "each"); set("[data-line-quantity]", data.quantity || 1);
        set("[data-line-cost]", data.cost || 0); set("[data-line-markup]", data.markup || 0);
        set("[data-line-price]", data.price || 0); set("[data-line-discount]", data.discount || 0);
        set("[data-line-tax]", data.tax ?? defaultTaxRate);
        lines.append(row); reindex(); calculate(); renderRcdIcons(row); return row;
    };

    workspace.querySelector("[data-quote-tabs]")?.addEventListener("click", (event) => {
        const tab = event.target.closest("[data-quote-tab]"); if (!tab) return;
        const selected = tab.dataset.quoteTab;
        workspace.querySelectorAll("[data-quote-tab]").forEach((item) => { const active = item === tab; item.classList.toggle("is-active", active); item.setAttribute("aria-selected", String(active)); });
        workspace.querySelectorAll("[data-quote-panel]").forEach((panel) => { panel.hidden = panel.dataset.quotePanel !== selected; });
        history.replaceState({}, "", selected === "preview" ? "#preview" : location.pathname + location.search);
    });

    workspace.addEventListener("click", (event) => {
        const remove = event.target.closest("[data-quote-line-remove]");
        if (remove) { remove.closest("[data-quote-line]")?.remove(); if (!lines?.children.length) addLine(); reindex(); calculate(); return; }
        if (event.target.closest("[data-quote-add-misc]")) { addLine({ source: "Miscellaneous", code: "Misc" })?.querySelector("[data-line-description]")?.focus(); return; }
        const item = event.target.closest("[data-quote-picker-item]");
        if (item) {
            addLine({ source: item.dataset.source, id: item.dataset.id, code: item.dataset.code, description: item.dataset.description, unit: item.dataset.unit, cost: item.dataset.cost, price: item.dataset.price, tax: item.dataset.tax });
            const dialog = item.closest("[data-rcd-dialog]"); if (dialog && window.rcdDialog) window.rcdDialog.close(dialog, "selected"); return;
        }
        if (event.target.closest("[data-quote-kit-add]")) {
            const dialog = event.target.closest("[data-rcd-dialog]");
            const name = dialog?.querySelector("[data-quote-kit-name]")?.value.trim();
            const description = dialog?.querySelector("[data-quote-kit-description]")?.value.trim();
            if (!name || !description) return;
            addLine({ source: "Kit", code: name, description, price: numberValue(dialog.querySelector("[data-quote-kit-price]")), tax: defaultTaxRate });
            if (window.rcdDialog) window.rcdDialog.close(dialog, "added");
        }
    });

    workspace.addEventListener("input", (event) => {
        if (event.target.matches("[data-line-markup], [data-line-cost]")) {
            const row = event.target.closest("[data-quote-line]");
            const markup = numberValue(row?.querySelector("[data-line-markup]"));
            if (markup > 0) row.querySelector("[data-line-price]").value = (numberValue(row.querySelector("[data-line-cost]")) * (1 + markup / 100)).toFixed(2);
        }
        if (event.target.closest("[data-quote-line]")) calculate();
        if (event.target.matches("[data-quote-picker-search]")) {
            const value = event.target.value.trim().toLowerCase();
            event.target.closest("[data-rcd-dialog]")?.querySelectorAll("[data-quote-picker-item]").forEach((item) => { item.hidden = !item.textContent.toLowerCase().includes(value); });
        }
    });

    taxMode?.addEventListener("change", calculate);

    workspace.querySelector("[data-rcd-customer-picker]")?.addEventListener("rcd:customer-picker:change", (event) => {
        const customerId = event.detail?.value || "";
        const site = workspace.querySelector("[data-quote-site]");
        site?.querySelectorAll("option[data-customer-id]").forEach((option) => { option.hidden = option.dataset.customerId !== customerId; });
        if (site?.selectedOptions[0]?.hidden) site.value = "";
    });

    workspace.querySelector("[data-quote-attachments]")?.addEventListener("change", (event) => {
        const list = workspace.querySelector("[data-quote-attachment-list]"); if (!list) return;
        list.innerHTML = [...event.target.files].map((file) => `<span class="rcd-badge neutral">${file.name.replace(/[<>&]/g, "")}</span>`).join("");
    });

    form?.addEventListener("submit", async (event) => {
        reindex();
        if (form.dataset.quoteUploadsReady === "true") return;
        const files = [...(workspace.querySelector("[data-quote-attachments]")?.files || [])];
        if (files.length === 0) return;
        event.preventDefault();
        const submit = form.querySelector('[type="submit"]'); if (submit) submit.disabled = true;
        try {
            for (const file of files) {
                if (file.size > 20 * 1024 * 1024) throw new Error(`${file.name} is larger than 20 MB.`);
                const body = new FormData(); body.append("file", file); body.append("panel", "quotes/attachments");
                const response = await fetch("/api/storage/upload", { method: "POST", body });
                const result = await response.json().catch(() => ({}));
                if (!response.ok || !result.publicUrl) throw new Error(result.message || `${file.name} could not be uploaded.`);
                const hidden = document.createElement("input"); hidden.type = "hidden"; hidden.name = "Form.AttachmentUrls"; hidden.value = result.publicUrl; form.append(hidden);
            }
            form.dataset.quoteUploadsReady = "true";
            form.requestSubmit(submit || undefined);
        } catch (error) {
            if (submit) submit.disabled = false;
            const alert = document.createElement("div"); alert.className = "rcd-alert warning"; alert.textContent = error.message; form.prepend(alert);
        }
    });
    if (!lines?.children.length) addLine(); else calculate();
    if (location.hash === "#preview") workspace.querySelector('[data-quote-tab="preview"]')?.click();
};
